/**
 * Cliente para API de remoção de background
 * Integração com backend Python (FastAPI + rembg)
 */

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

class BackendService {
  constructor() {
    this.apiUrl = API_URL;
    this.isAvailable = false;
  }

  /**
   * Verifica saúde da API
   */
  async checkHealth() {
    try {
      const response = await fetch(`${this.apiUrl}/health`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
      });

      if (response.ok) {
        this.isAvailable = true;
        const data = await response.json();
        console.log('✅ Backend disponível:', data);
        return { available: true, data };
      }

      this.isAvailable = false;
      return { available: false, error: 'Backend offline' };
    } catch (error) {
      console.warn('⚠️ Backend não disponível:', error.message);
      this.isAvailable = false;
      return { available: false, error: error.message };
    }
  }

  /**
   * Remove background de uma imagem
   */
  async removeBackground(file, options = {}) {
    const {
      model = 'u2net',
      alphaMatting = false,
      alphaMattingForegroundThreshold = 240,
      alphaMattingBackgroundThreshold = 10,
      alphaMattingErodeSize = 10,
    } = options;

    try {
      console.log(`🚀 Enviando para backend (modelo: ${model})...`);

      const formData = new FormData();
      formData.append('file', file);

      const params = new URLSearchParams({
        model,
        alpha_matting: alphaMatting.toString(),
        alpha_matting_foreground_threshold: alphaMattingForegroundThreshold.toString(),
        alpha_matting_background_threshold: alphaMattingBackgroundThreshold.toString(),
        alpha_matting_erode_size: alphaMattingErodeSize.toString(),
      });

      const startTime = performance.now();

      const response = await fetch(`${this.apiUrl}/remove-background?${params}`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erro ao processar imagem');
      }

      const blob = await response.blob();
      const duration = ((performance.now() - startTime) / 1000).toFixed(2);

      console.log(`✅ Processado em ${duration}s`);

      const processingTime = response.headers.get('X-Processing-Time');
      const modelUsed = response.headers.get('X-Model-Used');

      return {
        blob,
        duration: parseFloat(duration),
        processingTime: parseFloat(processingTime),
        model: modelUsed,
      };

    } catch (error) {
      console.error('❌ Erro ao processar:', error);
      throw error;
    }
  }

  /**
   * Converte blob para data URL
   */
  async blobToDataUrl(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Converte blob para ImageData
   */
  async blobToImageData(blob, width, height) {
    const url = URL.createObjectURL(blob);

    return new Promise((resolve, reject) => {
      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');

        ctx.drawImage(img, 0, 0, width, height);
        const imageData = ctx.getImageData(0, 0, width, height);

        URL.revokeObjectURL(url);
        resolve(imageData);
      };

      img.onerror = (error) => {
        URL.revokeObjectURL(url);
        reject(error);
      };

      img.src = url;
    });
  }

  /**
   * Converte blob para máscara (ImageData em grayscale)
   * Extrai o canal alpha de uma imagem PNG com transparência
   */
  async blobToMask(blob, width, height) {
    const url = URL.createObjectURL(blob);

    return new Promise((resolve, reject) => {
      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');

        ctx.drawImage(img, 0, 0, width, height);
        const imageData = ctx.getImageData(0, 0, width, height);

        // Cria máscara: converte canal alpha para grayscale
        const maskData = ctx.createImageData(width, height);

        for (let i = 0; i < imageData.data.length; i += 4) {
          const alpha = imageData.data[i + 3]; // Canal alpha

          // Máscara em grayscale (R=G=B=alpha)
          maskData.data[i] = alpha;     // R
          maskData.data[i + 1] = alpha; // G
          maskData.data[i + 2] = alpha; // B
          maskData.data[i + 3] = 255;   // A (opaco)
        }

        URL.revokeObjectURL(url);
        resolve(maskData);
      };

      img.onerror = (error) => {
        URL.revokeObjectURL(url);
        reject(error);
      };

      img.src = url;
    });
  }

  /**
   * Converte data URL para Blob
   */
  async dataUrlToBlob(dataUrl) {
    const response = await fetch(dataUrl);
    return await response.blob();
  }

  /**
   * Converte File/Blob para data URL
   */
  async fileToDataUrl(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * Lista modelos disponíveis
   */
  async listModels() {
    try {
      const response = await fetch(`${this.apiUrl}/models`);

      if (!response.ok) {
        throw new Error('Erro ao listar modelos');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('❌ Erro ao listar modelos:', error);
      return null;
    }
  }

  /**
   * Verifica se API está disponível
   */
  isApiAvailable() {
    return this.isAvailable;
  }

  /**
   * 🆕 Upload de imagem em resolução original
   * @param {File} file - Arquivo da imagem
   * @returns {Promise<{image_id: string, filename: string, size: {width: number, height: number}}>}
   */
  async uploadImage(file) {
    try {
      console.log(`📤 Upload de imagem: ${file.name}`);

      const formData = new FormData();
      formData.append('file', file);

      const startTime = performance.now();

      const response = await fetch(`${this.apiUrl}/upload`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erro ao fazer upload');
      }

      const data = await response.json();
      const duration = ((performance.now() - startTime) / 1000).toFixed(2);

      console.log(`✅ Upload concluído em ${duration}s - ID: ${data.image_id}`);

      return {
        ...data,
        uploadTime: parseFloat(duration),
      };

    } catch (error) {
      console.error('❌ Erro ao fazer upload:', error);
      throw error;
    }
  }

  /**
   * 🆕 Processa ajustes de cores em uma imagem
   * @param {string} imageId - ID da imagem (do upload)
   * @param {Object} adjustments - Ajustes de cores
   * @returns {Promise<{processed_id: string, download_url: string, processing_time: number}>}
   */
  async processColors(imageId, adjustments) {
    try {
      console.log(`🎨 Processando cores para imagem: ${imageId}`);
      console.log(`📊 Ajustes:`, adjustments);

      const startTime = performance.now();

      const response = await fetch(`${this.apiUrl}/process-colors`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          image_id: imageId,
          adjustments: adjustments,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erro ao processar cores');
      }

      const data = await response.json();
      const duration = ((performance.now() - startTime) / 1000).toFixed(2);

      console.log(`✅ Cores processadas em ${duration}s`);

      return {
        ...data,
        clientDuration: parseFloat(duration),
      };

    } catch (error) {
      console.error('❌ Erro ao processar cores:', error);
      throw error;
    }
  }

  /**
   * 🆕 Download de imagem processada
   * @param {string} imageId - ID da imagem processada
   * @returns {Promise<Blob>}
   */
  async downloadImage(imageId) {
    try {
      console.log(`📥 Download de imagem: ${imageId}`);

      const response = await fetch(`${this.apiUrl}/download/${imageId}`, {
        method: 'GET',
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erro ao fazer download');
      }

      const blob = await response.blob();

      console.log(`✅ Download concluído`);

      return blob;

    } catch (error) {
      console.error('❌ Erro ao fazer download:', error);
      throw error;
    }
  }

  /**
   * 🆕 Informações sobre uma imagem
   * @param {string} imageId - ID da imagem
   * @returns {Promise<Object>}
   */
  async getImageInfo(imageId) {
    try {
      const response = await fetch(`${this.apiUrl}/image-info/${imageId}`, {
        method: 'GET',
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erro ao buscar informações');
      }

      const data = await response.json();
      return data;

    } catch (error) {
      console.error('❌ Erro ao buscar informações:', error);
      throw error;
    }
  }

  /**
   * 🆕 Fluxo completo: Upload → Processar → Download
   * @param {File} file - Arquivo da imagem
   * @param {Object} adjustments - Ajustes de cores
   * @returns {Promise<Blob>}
   */
  async processImageComplete(file, adjustments) {
    try {
      // 1. Upload
      const uploadResult = await this.uploadImage(file);
      
      // 2. Processar cores
      const processResult = await this.processColors(uploadResult.image_id, adjustments);
      
      // 3. Download
      const blob = await this.downloadImage(processResult.processed_id);
      
      return {
        blob,
        uploadResult,
        processResult,
      };

    } catch (error) {
      console.error('❌ Erro no fluxo completo:', error);
      throw error;
    }
  }
}

// Singleton
const backendService = new BackendService();
export default backendService;
